import PropTypes from "prop-types";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// @mui
import { styled, alpha } from "@mui/material/styles";
import { Box, Drawer, Typography, Stack, Divider } from "@mui/material";

// hooks
import useResponsive from "../../../hooks/useResponsive";

// components
import NavSection from "../../../components/nav-section";

// Configuración de navegación
import navConfig from "./config";
import navConfigTesoreria from "./configTesoreria";
import navConfigGestor from "./configGestor";
import navConfigNuevoUsuario from "./configNuevoUsuario";
import navConfigDescarga from "./configDescarga";

import Cookie from "universal-cookie";
import Scrollbar from "simplebar-react";

// Logo
import IcontecWH from "../../../img/LOGO-Icontec-2019_hor_white (1).png";

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const cookie = new Cookie();

// ----------------------------------------------------------------------
// HEADER DEL SIDEBAR
// ----------------------------------------------------------------------

const StyledHeader = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  padding: theme.spacing(3, 2.5),

  background: `
    linear-gradient(
      145deg,
      #002E5D 0%,
      #003F73 55%,
      #0085CA 140%
    )
  `,

  color: "#fff",

  "&::before": {
    content: '""',
    position: "absolute",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: alpha("#fff", 0.04),
    top: -100,
    right: -70,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: alpha("#0085CA", 0.12),
    bottom: -70,
    left: -50,
  },
}));

// ----------------------------------------------------------------------
// CONTENEDOR DE NAVEGACIÓN
// ----------------------------------------------------------------------

const NavigationContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),

  "& .simplebar-scrollbar::before": {
    backgroundColor: alpha("#0085CA", 0.45),
  },

  "& .simplebar-track.simplebar-vertical": {
    width: 6,
  },
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

// ----------------------------------------------------------------------

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();

  const isDesktop = useResponsive("up", "lg");

  // ------------------------------------------------------------
  // Cierra el menú cuando cambia la ruta
  // ------------------------------------------------------------

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  // ------------------------------------------------------------
  // Tipo de usuario
  // ------------------------------------------------------------

  const tipoUsuario = cookie.get("tipoUsuario");

  // ------------------------------------------------------------
  // CONTENIDO DEL SIDEBAR
  // ------------------------------------------------------------

  const renderContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F8FAFC",
      }}
    >
      {/* =====================================================
          HEADER
      ====================================================== */}

      <StyledHeader>
        <Stack
          spacing={1.2}
          alignItems="center"
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* LOGO */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              width: "100%",

              mb: 0.5,
            }}
          >
            <Box
              component="img"
              src={IcontecWH}
              alt="ICONTEC"
              sx={{
                width: 175,
                maxWidth: "90%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* DIVISOR */}

          <Box
            sx={{
              width: "65%",
              height: "1px",
              backgroundColor: alpha("#fff", 0.25),
            }}
          />

          {/* NOMBRE DEL SISTEMA */}

          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: "#fff",
              lineHeight: 1.2,
            }}
          >
            Huella
          </Typography>

          <Typography
            sx={{
              fontSize: "11px",
              color: alpha("#fff", 0.72),
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            Plataforma de certificados
          </Typography>
        </Stack>
      </StyledHeader>

      {/* =====================================================
          NAVEGACIÓN
      ====================================================== */}

      <NavigationContainer
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Scrollbar
          sx={{
            height: "100%",

            "& .simplebar-content": {
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              px: 0.3,
            }}
          >
            {/* -------------------------------------------------
                ADMINISTRADOR
            -------------------------------------------------- */}

            {tipoUsuario == 1 ? (
              <NavSection data={navConfig} />
            ) : /* -------------------------------------------------
                TESORERÍA
            -------------------------------------------------- */

            tipoUsuario == 2 ? (
              <NavSection data={navConfigTesoreria} />
            ) : /* -------------------------------------------------
                GESTOR
            -------------------------------------------------- */

            tipoUsuario == 5 || tipoUsuario == 6 ? (
              <NavSection data={navConfigGestor} />
            ) : /* -------------------------------------------------
                NUEVO USUARIO
            -------------------------------------------------- */

            tipoUsuario == 7 ? (
              <NavSection data={navConfigNuevoUsuario} />
            ) : /* -------------------------------------------------
                DESCARGA
            -------------------------------------------------- */

            tipoUsuario == 8 ? (
              <NavSection data={navConfigDescarga} />
            ) : (
              <Box />
            )}
          </Box>
        </Scrollbar>
      </NavigationContainer>

      {/* =====================================================
          FOOTER
      ====================================================== */}

      <Box
        sx={{
          px: 2,
          py: 1.8,
          backgroundColor: "#fff",
          borderTop: "1px solid",
          borderColor: alpha("#002E5D", 0.08),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#0085CA",
              boxShadow: "0 0 0 4px rgba(0,133,202,0.08)",
            }}
          />

          <Typography
            sx={{
              fontSize: "11px",
              color: "#6B7280",
              fontWeight: 500,
              letterSpacing: "0.3px",
            }}
          >
            Sistema Huella
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  // ============================================================
  // DESKTOP
  // ============================================================

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: {
          lg: 0,
        },

        width: {
          lg: NAV_WIDTH,
        },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,

              backgroundColor: "#F8FAFC",

              border: "none",

              borderRight: "1px solid",
              borderColor: alpha("#002E5D", 0.08),

              boxShadow: "4px 0 20px rgba(0, 46, 93, 0.04)",

              overflow: "hidden",
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,

              backgroundColor: "#F8FAFC",

              border: "none",

              boxShadow: "8px 0 30px rgba(0, 46, 93, 0.15)",

              overflow: "hidden",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
